import React, { useEffect, useState } from "react";
import {
  getSubSectionsList,
  deleteSubSection,
} from "../../../services/subSectionsService";
import { paginateData, calculateTotalPages } from "../../../assets/js/script";
import TableHeader from "../../Common/TableComponent/TableHeader";
import TableToolbar from "../../Common/TableComponent/TableToolbar";
import TablesRow from "../../Common/TableComponent/TablesRow";
import { renderStatusBadge } from "../../Common/TableComponent/tableBadges";
import { Pagination } from "../../Common/TableComponent/Pagination";
import { AddSubSection } from "./AddSubSection";
import { Loading } from "../../Common/OtherElements/Loading";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";

const getSubSectionId = (item) => item.id ?? item.Id;

const getSectionName = (item) => {
  const name = item.sectionName ?? item.SectionName;
  if (name === null || name === undefined || String(name).trim() === "") {
    return "-";
  }
  return String(name);
};

const getSubSectionName = (item) =>
  item.subSectionName || item.SubSectionName || "-";

const getSubSectionSlug = (item) =>
  item.subSectionSlug || item.SubSectionSlug || "-";

const getDisplayOrder = (item) =>
  item.displayOrder ?? item.DisplayOrder ?? "-";

const isTruthyFlag = (item, ...keys) => {
  for (const key of keys) {
    const value = item[key];
    if (value === true || value === "true" || value === 1) return true;
  }
  return false;
};

const renderPremiumBadge = (isOn) => (
  <span className={`badge badge-soft-${isOn ? "warning" : "secondary"}`}>
    {isOn ? "Premium" : "-"}
  </span>
);

const renderYesNoBadge = (isOn) => (
  <span className={`badge badge-soft-${isOn ? "success" : "secondary"}`}>
    {isOn ? "Yes" : "No"}
  </span>
);

export const ManageSubSectionsContent = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "manage-sub-sections";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [subSectionsList, setSubSectionsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubSection, setSelectedSubSection] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.addAccess && !pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getSubSectionsList();
      const list = response.data?.result || response.data || [];
      setSubSectionsList(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = subSectionsList.filter((item) => {
    const section = getSectionName(item).toLowerCase();
    const name = getSubSectionName(item).toLowerCase();
    const slug = getSubSectionSlug(item).toLowerCase();
    const query = searchQuery.toLowerCase();
    return (
      section.includes(query) ||
      name.includes(query) ||
      slug.includes(query)
    );
  });

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (subSectionId) => {
    const confirmed = await confirmDelete("Sub Section");
    if (confirmed) {
      try {
        await deleteSubSection(subSectionId);
        Swal.fire(
          "Deleted!",
          "The sub section has been deleted successfully.",
          "success"
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  return (
    <>
      <ComponentHeader title="Sub Sections" />

      {pageAccessDetails.addAccess ? (
        <AddSubSection
          editMode={editMode}
          initialData={selectedSubSection ?? {}}
          onSuccess={fetchData}
          setSelectedSubSection={setSelectedSubSection}
          setEditMode={setEditMode}
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
        />
      ) : null}

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5 xc-list-card">
              <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <h5 className="mb-0">Manage Sub Sections</h5>
                  <span className="xc-list-count">
                    {filteredData.length}{" "}
                    {filteredData.length === 1
                      ? "sub section"
                      : "sub sections"}
                  </span>
                </div>
                {pageAccessDetails.addAccess ? (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setSelectedSubSection(null);
                      setEditMode(false);
                      setIsFormOpen(true);
                    }}
                  >
                    <i className="ri-add-line me-1" aria-hidden="true" />
                    Add Sub Section
                  </button>
                ) : null}
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <TableToolbar
                  entriesPerPage={entriesPerPage}
                  onEntriesChange={handleEntriesChange}
                  searchValue={searchQuery}
                  searchPlaceholder="Search sub sections..."
                  onSearchChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  onRefresh={fetchData}
                />

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "Sl No",
                          "Section Name",
                          "Sub Section Name",
                          "Slug",
                          "Featured",
                          "Featured Home",
                          "Premium",
                          "Display Order",
                          "Status",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="10" />
                        ) : (
                          currentData.map((item, index) => (
                            <TablesRow
                              key={getSubSectionId(item)}
                              rowData={{
                                slNo:
                                  (currentPage - 1) * entriesPerPage +
                                  index +
                                  1,
                                sectionName: getSectionName(item),
                                subSectionName: getSubSectionName(item),
                                slug: getSubSectionSlug(item),
                                featured: renderYesNoBadge(
                                  isTruthyFlag(
                                    item,
                                    "isFeatured",
                                    "IsFeatured",
                                    "featured",
                                    "Featured"
                                  )
                                ),
                                featuredHome: renderYesNoBadge(
                                  isTruthyFlag(
                                    item,
                                    "isFeaturedHome",
                                    "IsFeaturedHome",
                                    "featuredHome",
                                    "FeaturedHome"
                                  )
                                ),
                                premium: renderPremiumBadge(
                                  isTruthyFlag(
                                    item,
                                    "isPremium",
                                    "IsPremium"
                                  )
                                ),
                                displayOrder: getDisplayOrder(item),
                                status: renderStatusBadge(item),
                              }}
                              columns={[
                                "slNo",
                                "sectionName",
                                "subSectionName",
                                "slug",
                                "featured",
                                "featuredHome",
                                "premium",
                                "displayOrder",
                                "status",
                              ]}
                              hideIcons={false}
                              onEdit={() => {
                                setSelectedSubSection(item);
                                setEditMode(true);
                                setIsFormOpen(true);
                              }}
                              onDelete={() =>
                                handleDelete(getSubSectionId(item))
                              }
                              pageLevelAccessData={pageAccessDetails}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};
